import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import SEO from "@/components/SEO";
import GoogleSignInButton from "@/components/GoogleSignInButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";

const Auth = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { user, loading } = useAuth();

  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const redirectTo = (location.state as { from?: string } | null)?.from ?? "/";

  useEffect(() => {
    if (!loading && user) navigate(redirectTo, { replace: true });
  }, [loading, user, navigate, redirectTo]);

  const signInWithGoogle = async () => {
    setBusy(true);
    sessionStorage.setItem("auth:redirect", redirectTo);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: `${window.location.origin}/auth/callback` },
    });
    if (error) {
      setBusy(false);
      toast({ title: t("auth.errorTitle"), description: error.message, variant: "destructive" });
    }
  };

  const submitEmail = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    if (mode === "signin") {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      setBusy(false);
      if (error) {
        toast({ title: t("auth.errorTitle"), description: error.message, variant: "destructive" });
        return;
      }
      navigate(redirectTo, { replace: true });
      return;
    }

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: `${window.location.origin}/auth/callback` },
    });
    setBusy(false);
    if (error) {
      toast({ title: t("auth.errorTitle"), description: error.message, variant: "destructive" });
      return;
    }
    if (!data.session) {
      toast({ title: t("auth.checkEmailTitle"), description: t("auth.checkEmailText") });
    }
  };

  return (
    <>
      <SEO title={t("auth.seoTitle")} description={t("auth.seoDescription")} noindex />
      <main className="min-h-screen bg-background px-6 pb-20 pt-32">
        <div className="mx-auto w-full max-w-md space-y-8">
          <header className="space-y-3 text-center">
            <h1 className="font-serif text-3xl text-foreground sm:text-4xl">{t("auth.title")}</h1>
            <p className="text-muted-foreground">{t("auth.subtitle")}</p>
          </header>

          <div className="space-y-6 rounded-2xl border border-border bg-card p-6 shadow-soft sm:p-8">
            <GoogleSignInButton label={t("auth.google")} onClick={signInWithGoogle} disabled={busy} />

            <div className="flex items-center gap-4">
              <span className="h-px flex-1 bg-border" />
              <span className="text-xs uppercase tracking-widest text-muted-foreground">{t("auth.or")}</span>
              <span className="h-px flex-1 bg-border" />
            </div>

            <form onSubmit={submitEmail} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="auth-email">{t("auth.email")}</Label>
                <Input
                  id="auth-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="auth-password">{t("auth.password")}</Label>
                <Input
                  id="auth-password"
                  type="password"
                  autoComplete={mode === "signin" ? "current-password" : "new-password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  minLength={6}
                  required
                />
              </div>
              <Button type="submit" size="lg" className="w-full" disabled={busy}>
                {mode === "signin" ? t("auth.signIn") : t("auth.signUp")}
              </Button>
            </form>

            <button
              type="button"
              className="w-full text-sm text-muted-foreground underline-offset-4 hover:text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded"
              onClick={() => setMode(mode === "signin" ? "signup" : "signin")}
            >
              {mode === "signin" ? t("auth.toSignUp") : t("auth.toSignIn")}
            </button>
          </div>

          <p className="text-center text-xs text-muted-foreground">
            {t("auth.legal")}{" "}
            <Link to="/terminos" className="underline underline-offset-4 hover:text-primary">
              {t("footer.terms")}
            </Link>
          </p>
        </div>
      </main>
    </>
  );
};

export default Auth;
